import { debounce } from "lodash";
import React, { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import NotificationAlert from "@/Components/common/client/header/notification/NotificationAlert";
import {
  newMessageReceived,
  updateTypingStatus,
  updateReadReceipt,
  markChatAsRead,
  sendNotificationToCandidate,
  fetchSingleCandidateChatList,
} from "@/reducers/chat";
import { handleSocketConnection } from "@/reducers/socket";

import {
  fetchUpcomingBookings,
  fetchUpcomingCandidateDashbord,
  updateDashboardUpcomingPagination,
} from "../reducers/clientdashboard";
import {
  apiEncrypt,
  getDecryptedLocalStorageValue,
} from "../utilities/keyEncryption";

const SocketLayout = ({ children }) => {
  const { socket } = useSelector((state) => state.socket);
  const { user } = useSelector((state) => state.clientProfile);
  const { upComingPagination } = useSelector((state) => state.clientDashboard);
  const { chatUsers } = useSelector((state) => state.chat);
  const { session } = useSelector((state) => state.session);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const renderOnce = useRef(false);
  useEffect(() => {
    if (renderOnce.current) {
      return;
    }
    const { user_id, user_type, user_token, client_id } = session;
    if (socket && socket !== null && typeof socket.disconnect === "function") {
      // if socket is already connected, disconnect it
      socket.disconnect();
    }
    if (user_id && user_type && user_token && client_id) {
      renderOnce.current = true;
      dispatch(
        handleSocketConnection({
          user_id: client_id,
          user_type,
          user_token,
        }),
      );
    }
  }, [session]);

  const showLiveNotification = (data) => {
    const { note_id, message, msgLink, msg_type, unread } = data;
    toast(
      (t) => {
        return <NotificationAlert t={t} live_notifications={data} />;
      },
      {
        position: "top-right",
        className: "bg-white border-0 !p-0 [&>div]:m-0",
        duration: 5000,
        id: message.trim(),
      },
    );
  };

  const updateSocketListeners = () => {
    socket.removeAllListeners();

    // socket.on("duplicate_login", function (data) {
    //   navigate("/logout");
    // });

    // Candidate Opened Chat
    socket.on("updateUnreadChat", function (data) {
      const { receiver_id, sender_id, send_from } = data;
      if (
        receiver_id &&
        sender_id &&
        send_from === "Client" &&
        sender_id == user.client_id
      ) {
        dispatch(updateReadReceipt(data));
        const _data = {
          senderId: sender_id,
          sendFrom: "Client",
          sendTo: "Candidate",
          receiverId: receiver_id,
        };
        dispatch(markChatAsRead(_data));
      }
    });

    // calling dashboard upcoming actions
    const dispatchUpcomingActions = () => {
      dispatch(fetchUpcomingBookings({ for_upcomming: 0 }));
      dispatch(
        updateDashboardUpcomingPagination({
          ...upComingPagination,
          currentPage: 1,
          isLastPage: false,
        }),
      );
      dispatch(fetchUpcomingCandidateDashbord({}));
    };

    socket.on("shiftStartNotification", function (data) {
      if (data?.type === "shift_start" && window.location.pathname === "/") {
        dispatchUpcomingActions();
      }
    });

    // Typing event
    socket.on("typing", function (data) {
      if (data.receiver_id && data.sender_name && data.send_to === "Client") {
        const senderObj = chatUsers.find(
          (user) => user.id === parseInt(data.sender_id),
        );
        if (senderObj) {
          const debouncedUpdateTyping = debounce(() => {
            dispatch(
              updateTypingStatus({
                sender_id: senderObj.id,
                typing: false,
              }),
            );
          }, 800); // Timeout

          dispatch(
            updateTypingStatus({
              sender_id: senderObj.id,
              typing: true,
            }),
          );
          debouncedUpdateTyping(); // Call immediately for initial typing state
        }
      }
    });
    socket.on("live_notifications", function (data) {
      showLiveNotification(data);
    });

    // for connection live..
    socket.on("ping", () => {
      socket.emit("pong"); // Send custom pong back
    });

    // receive message from candidate
    socket.on("getLastMessageInWebapp", function (data) {
      let sender_id = data.sender_id;
      if (data.receiver_id == user.client_id) {
        let isUserExistInList = chatUsers.filter((user) => {
          return user.id == sender_id;
        });
        // dispatch(getTotalUnreadCount());
        if (isUserExistInList.length) {
          // If Candidate Exists in Chat User Lists Then Adding Messgae In List and Chat Screen If Open
          dispatch(newMessageReceived(data));
        } else {
          // If Candidate Doesn't Exists not in list then Fetching Candiate List Data API
          dispatch(fetchSingleCandidateChatList(sender_id));
        }
      }
    });

    //send notification to candidate and in response of api - emit message to candidate
    const channel = new BroadcastChannel("notification_channel");

    socket.on("sendNotificationToCandidate", function (data) {
      const { notificationData } = data;

      let processedMessages =
        getDecryptedLocalStorageValue("processedMessages") || [];

      if (processedMessages.includes(notificationData.message_id)) {
        // console.log("Duplicate message detected. Ignoring...");
        return;
      }

      processedMessages.push(notificationData.message_id);
      if (processedMessages.length > 40) {
        processedMessages.shift();
      }
      const encryptedValue = apiEncrypt(JSON.stringify(processedMessages));
      localStorage.setItem("processedMessages", encryptedValue);

      // Broadcast the message ID to other tabs
      channel.postMessage(notificationData.message_id);

      if (
        notificationData.sender_id == parseInt(user.client_id) &&
        notificationData?.client_user_id == parseInt(user?.id)
      ) {
        dispatch(sendNotificationToCandidate(data));
      }
    });

    // Listen for messages from other tabs
    channel.onmessage = (event) => {
      let processedMessages =
        getDecryptedLocalStorageValue("processedMessages") || [];
      if (!processedMessages.includes(event.data)) {
        processedMessages.push(event.data);
        if (processedMessages.length > 40) {
          processedMessages.shift();
        }
        const encryptedValue = apiEncrypt(JSON.stringify(processedMessages));
        localStorage.setItem("processedMessages", encryptedValue);
      }
    };
  };
  useEffect(() => {
    if (socket && chatUsers && chatUsers.length > 0) {
      updateSocketListeners();
    }
  }, [chatUsers]);
  useEffect(() => {
    if (socket && socket !== null) {
      updateSocketListeners();
    }
  }, [socket]);
  return children;
};

export default SocketLayout;
